import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';
import { Stars } from '@/components/Stars';
import { Send, CheckCircle2, MapPin, Ticket, Loader2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.contact.required;
    if (!form.email.trim()) e.email = t.contact.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.contact.invalidEmail;
    if (!form.subject.trim()) e.subject = t.contact.required;
    if (!form.message.trim()) e.message = t.contact.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success(t.contact.success);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const field = (key, type = 'text') => (
    <div>
      <label className="block text-xs uppercase tracking-wide text-white/55 mb-1.5">{t.contact[key]}</label>
      <input
        data-testid={`contact-${key}`}
        type={type}
        value={form[key]}
        onChange={(e) => update(key, e.target.value)}
        className={`w-full rounded-xl border bg-[#040f0c] px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[var(--cea-accent)] ${errors[key] ? 'border-red-500' : 'border-[var(--cea-border)]'}`}
      />
      {errors[key] && <p className="mt-1 text-xs text-red-400" data-testid={`contact-error-${key}`}>{errors[key]}</p>}
    </div>
  );

  return (
    <section id="contacte" data-testid="contact-section" className="relative overflow-hidden scroll-mt-20 py-20 sm:py-28 bg-[#040f0c]">
      <Stars count={28} seed={7} />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="text-xs font-bold tracking-[0.18em] text-[var(--cea-accent)] uppercase">{t.contact.tag}</span>
          <h2 className="mt-3 font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-[-0.01em]">{t.contact.title}</h2>
          <p className="mt-4 text-white/70">{t.contact.subtitle}</p>

          <div className="mt-8 rounded-2xl border border-[var(--cea-border)] bg-[#0b241b] p-5 space-y-3">
            <p className="text-xs uppercase tracking-wide text-white/45">{t.contact.infoTitle}</p>
            <div className="flex items-center gap-3 text-sm text-white/85"><MapPin size={16} className="text-[var(--cea-accent)]" /> {t.contact.infoPlace}</div>
            <div className="flex items-center gap-3 text-sm text-white/85"><Ticket size={16} className="text-[var(--cea-accent)]" /> {t.contact.infoFree}</div>
            <a href="mailto:info@canilloescenicarts.com" data-testid="contact-info-email" className="flex items-center gap-3 text-sm text-white/85 hover:text-[var(--cea-accent)] transition-colors duration-200"><Mail size={16} className="text-[var(--cea-accent)]" /> info@canilloescenicarts.com</a>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="h-full flex flex-col items-center justify-center text-center rounded-2xl border border-[var(--cea-border-strong)] bg-[#0b241b] p-10 cea-glow-soft">
              <CheckCircle2 size={56} className="text-[var(--cea-accent)]" />
              <h3 className="mt-4 font-display font-medium text-2xl">{t.contact.success}</h3>
              <p className="mt-2 text-white/65">{t.contact.successSub}</p>
              <button onClick={() => setDone(false)} data-testid="contact-another" className="glow-btn mt-6 rounded-xl px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--cea-accent)]">
                {t.contact.another}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-[var(--cea-border)] bg-[#0b241b] p-6 sm:p-8 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('name')}
                {field('email', 'email')}
              </div>
              {field('subject')}
              <div>
                <label className="block text-xs uppercase tracking-wide text-white/55 mb-1.5">{t.contact.message}</label>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  className={`w-full rounded-xl border bg-[#040f0c] px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[var(--cea-accent)] resize-none ${errors.message ? 'border-red-500' : 'border-[var(--cea-border)]'}`}
                />
                {errors.message && <p className="mt-1 text-xs text-red-400" data-testid="contact-error-message">{errors.message}</p>}
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="glow-btn inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--cea-accent)] disabled:opacity-60"
              >
                {sending ? <><Loader2 size={18} className="animate-spin" /> {t.contact.sending}</> : <><Send size={18} /> {t.contact.send}</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
